import React, { useMemo } from 'react';
import './OtpInput.css';

export type Props = {
    value: string;
    valueLength: number;
    onChange: (value: string) => void;
};

export default function OtpInput({ value, valueLength, onChange }: Props) {
    const RE_DIGIT = new RegExp(/^\d+$/);
    const valueItems = useMemo(() => {
        const safeValue = value || ""; // Ensure value is always a string
        const valueArray = safeValue.split('');
        const items: Array<string> = [];

        for (let i = 0; i < valueLength; i++) {
            const char = valueArray[i];

            if (RE_DIGIT.test(char)) {
                items.push(char);
            } else {
                items.push('');
            }
        }

        return items;
    }, [value, valueLength]);

    const focusToNextInput = (target: HTMLElement) => {
        const nextElementSibling =
            target.nextElementSibling as HTMLInputElement | null;

        if (nextElementSibling) {
            nextElementSibling.focus();
        }
    };
    
    const focusToPrevInput = (target: HTMLElement) => {
        const previousElementSibling =
            target.previousElementSibling as HTMLInputElement | null;

        if (previousElementSibling) {
            previousElementSibling.focus();
        }
    };

    const inputOnChange = (e: React.ChangeEvent<HTMLInputElement>, idx: number) => {
        const target = e.target;
        let targetValue = target.value.trim();
        const isTargetValueDigit = RE_DIGIT.test(targetValue);

        if (!isTargetValueDigit && targetValue !== '') {
            return;
        }

        const safeValue = value || ""; // Ensure value is always a string

        if (isTargetValueDigit && targetValue.length === 1) {
            // Create new value by inserting the new digit at the current index
            const newValue = safeValue.substring(0, idx) + targetValue + safeValue.substring(idx + 1);
            onChange(newValue);
            focusToNextInput(target);
        } else if (targetValue.length === valueLength) {
            // Handle pasting a complete OTP
            onChange(targetValue);
            target.blur();
        }
    };

    const inputOnKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        const { key } = e;
        const target = e.target as HTMLInputElement;
        const idx = Array.from(target.parentNode!.children).indexOf(target);
        const safeValue = value || "";

        if (key === 'ArrowRight' || key === 'ArrowDown') {
            e.preventDefault();
            return focusToNextInput(target);
        }

        if (key === 'ArrowLeft' || key === 'ArrowUp') {
            e.preventDefault();
            return focusToPrevInput(target);
        }

        // Handle backspace - remove the current digit and shift remaining digits left
        if (key === 'Backspace') {
            e.preventDefault();
            
            // If current input has value, clear it and shift all following digits left
            if (target.value !== '') {
                // Remove the current digit and shift all following digits left
                let newValue = '';
                
                // Build the new value by removing the current digit and shifting remaining digits
                for (let i = 0; i < safeValue.length; i++) {
                    if (i === idx) continue; // Skip the current index
                    if (i > idx) {
                        // Put this digit in the previous position
                        newValue += safeValue[i];
                    } else {
                        newValue += safeValue[i];
                    }
                }
                
                // If we removed the last digit, add an empty position at the end
                if (newValue.length < safeValue.length) {
                    newValue = newValue.padEnd(safeValue.length - 1, '');
                }
                
                onChange(newValue);
            } else if (idx > 0) {
                // If current input is empty, focus on previous input
                focusToPrevInput(target);
            }
            return;
        }

        const targetValue = target.value;
        target.setSelectionRange(0, targetValue.length);
    };

    const inputOnFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        const { target } = e;
        target.setSelectionRange(0, target.value.length);
    };

    return (
        <div className="otp-group">
            {valueItems.map((digit: any, idx: number) => (
                <input
                    key={idx}
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    pattern="\d{1}"
                    maxLength={valueLength}
                    className="otp-input"
                    value={digit}
                    onChange={(e) => inputOnChange(e, idx)}
                    onKeyDown={inputOnKeyDown}
                    onFocus={inputOnFocus}
                />
            ))}
        </div>
    );
}